import { yupResolver } from '@hookform/resolvers/yup';
import { Icon } from '@iconify/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import type { FC, PropsWithChildren } from 'react';
import { useState } from 'react';
import { Controller } from 'react-hook-form';
import * as yup from 'yup';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import useForm from '@/hooks/useForm';
import { queries } from '@/queries';
import { type Status, status as commonStatus } from '@/services/common';
import type { DeliveryMethod } from '@/services/externalCandidates';
import {
  deliveryMethodSchema,
  sendMessagePackageToCandidates,
} from '@/services/externalCandidates';
import { capitalizeFirstLetter } from '@/utils/cvGenerator';

import Button from '../Button';
import CircularLoadingIndicator from '../CircularLoadingIndicator';
import ConfirmationDialog from '../ConfirmationDialog';
import MultiLineInput from '../Form/Fields/MultiLineInput';
import LoadingPlaceholder from '../LoadingPlaceholder';
import Text from '../Text';
import { ToggleGroup, ToggleItem } from '../ui/toggle-group';

type FormData = yup.InferType<typeof schema>;

const defaultValues: FormData = {
  messageBody: '',
  deliveryMethod: 'EMAIL',
};

type Props = {
  candidates: number[];
};

const SendMessage: FC<PropsWithChildren<Props>> = ({
  candidates,
  children,
}) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const queryClient = useQueryClient();

  const {
    mutate,
    isLoading,
    data,
    reset: resetData,
  } = useMutation({
    mutationFn: sendMessagePackageToCandidates,
    mutationKey: ['sendMessagePackageToCandidates'],
    onSuccess: () => {
      queryClient.invalidateQueries(queries.externalCandidates.list._def);
      reset(defaultValues);
    },
  });

  const {
    register,
    handleSubmit,
    trigger,
    formState: { errors, isValid },
    reset,
    control,
  } = useForm<FormData>({
    defaultValues,
    resolver: yupResolver(schema),
  });

  const deliveryMethodOptions = deliveryMethodSchema.options.map((option) => ({
    value: option,
    label: capitalizeFirstLetter(option),
  }));

  return (
    <Dialog
      onOpenChange={() => {
        reset(defaultValues);
        resetData();
      }}
    >
      <DialogTrigger>{children}</DialogTrigger>
      <DialogContent className="text-neutral-1000">
        {!data?.packageId && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              trigger();
              if (isValid) setShowConfirmDialog(true);
            }}
            className="flex flex-col gap-2"
          >
            <DialogHeader>Message</DialogHeader>
            <DialogTitle>
              Send message to{' '}
              <span className="text-primary-500">{candidates.length}</span>{' '}
              candidates
            </DialogTitle>
            <Controller
              control={control}
              name="deliveryMethod"
              render={({ field: { onChange, value } }) => (
                <ToggleGroup
                  type="single"
                  value={value}
                  onValueChange={onChange}
                  variant="secondary"
                >
                  {deliveryMethodOptions.map((option) => (
                    <ToggleItem key={option.value} value={option.value}>
                      {option.label}
                    </ToggleItem>
                  ))}
                </ToggleGroup>
              )}
            />
            <MultiLineInput
              {...register('messageBody')}
              variant="light"
              error={errors.messageBody?.message}
            />
            <Button
              className="text-neutral-0"
              variant="primaryLight"
              type="submit"
              isLoading={isLoading}
            >
              Send
            </Button>
            <ConfirmationDialog
              confirmMessage={`Are you sure you want to send a message to ${candidates.length} candidates?`}
              onConfirm={handleSubmit((formData) => {
                mutate({
                  candidates,
                  messageBody: formData.messageBody,
                  deliveryMethod: [formData.deliveryMethod],
                });
              })}
              open={showConfirmDialog}
              onOpenChange={setShowConfirmDialog}
            />
          </form>
        )}
        {data?.packageId && (
          <>
            <DialogHeader>Sending</DialogHeader>
            <MessagesPackageSummary id={data.packageId} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const MessagesPackageSummary = ({ id }: { id: number }) => {
  const { data, isLoading } = useQuery({
    ...queries.externalCandidates.getMessagesPackage(id),
    refetchInterval: (queryData) =>
      queryData?.status === 'PROCESSING' ? 2 * 1000 : false,
  });

  const statusIcon = {
    PROCESSING: () => <CircularLoadingIndicator size="sm" />,
    FAILED: () => (
      <Icon icon="icon-park-outline:message-failed" className="text-error" />
    ),
    READY: () => (
      <Icon icon="material-symbols:check-box" className="text-success" />
    ),
  };

  const statusCount = (status: Status) =>
    data?.messages.filter((message) => message.status === status).length ?? 0;

  if (isLoading) return <LoadingPlaceholder />;
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-4 rounded-lg bg-neutral-200 px-6 py-2">
        <Text
          variant="h5"
          className="w-full text-center font-semibold text-primary-600"
        >
          {capitalizeFirstLetter(data?.messages?.[0]?.type ?? '')}
        </Text>
        <div className="flex flex-row justify-between gap-2">
          {commonStatus.map((singleStatus) => (
            <div
              key={singleStatus}
              className="flex flex-row items-center gap-1"
            >
              {singleStatus !== 'PROCESSING' && statusIcon[singleStatus]()}
              <Text className="text-neutral-800">
                {capitalizeFirstLetter(singleStatus)}:
              </Text>
              <Text
                className={classNames('font-semibold', {
                  'text-success': singleStatus === 'READY',
                  'text-error': singleStatus === 'FAILED',
                  'text-primary-600': singleStatus === 'PROCESSING',
                })}
              >
                {statusCount(singleStatus)}
              </Text>
            </div>
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-4">
        {data?.messages.map(({ candidateName, status, id: messageId }) => (
          <div
            key={messageId}
            className="flex flex-row items-center justify-between"
          >
            <div className="flex flex-row items-center gap-1">
              {statusIcon[status]()}
              <Text>{candidateName}</Text>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const schema = yup.object({
  messageBody: yup.string().required(),
  deliveryMethod: yup
    .mixed<DeliveryMethod>()
    .oneOf(deliveryMethodSchema.options)
    .required(),
});
export default SendMessage;
